import { useEffect, useId, useRef, useState } from 'react';
import { accountsApi, banksApi, lookupsApi, templatesApi } from '../api/services';
import type { AccountDto, BankAccountDto, CurrencyDto, TemplateDto } from '../api/types';
import { Select, useAsync } from './components';

/** Todas las cuentas de detalle activas de la empresa, cargadas una vez por sesión. */
let accountCache: Promise<AccountDto[]> | null = null;
export function invalidateAccountCache() { accountCache = null; }
function loadAccounts() {
  return (accountCache ??= accountsApi.detailLookup(undefined, 5000).catch((e) => { accountCache = null; throw e; }));
}

/**
 * Selector de cuenta: sólo se elige entre las cuentas de detalle activas ya cargadas en el catálogo (no se digita).
 * Al abrirlo se puede filtrar por código o nombre.
 */
export function AccountPicker({ value, onChange, placeholder = 'Seleccione la cuenta', disabled }: { value: string; onChange: (v: string) => void; placeholder?: string; disabled?: boolean }) {
  const [accounts, setAccounts] = useState<AccountDto[] | null>(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const listId = useId();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  // La lista se dibuja con posición fija para que no la recorten las tablas con desplazamiento.
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = buttonRef.current?.getBoundingClientRect();
      if (r) setPos({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 320) });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [open]);
  useEffect(() => {
    let alive = true;
    loadAccounts().then((a) => { if (alive) setAccounts(a); }).catch(() => { if (alive) setAccounts([]); });
    return () => { alive = false; };
  }, []);
  const selected = accounts?.find((a) => a.number === value);
  const f = filter.trim().toLowerCase();
  const matches = (accounts ?? []).filter((a) => !f || a.number.toLowerCase().includes(f) || a.name.toLowerCase().includes(f)).slice(0, 200);
  const pick = (n: string) => { onChange(n); setOpen(false); setFilter(''); };
  const label = selected ? `${selected.number} — ${selected.name}`
    : value ? `${value} (no existe o está inactiva)` : accounts === null ? 'Cargando cuentas…' : placeholder;
  return (
    <div style={{ position: 'relative' }} onBlur={(e: { currentTarget: HTMLElement; relatedTarget: EventTarget | null }) => {
      // Se cierra sólo cuando el foco sale del selector (no al pasar del botón al buscador).
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
    }}>
      <button ref={buttonRef} type="button" className="acc-input" disabled={disabled} aria-haspopup="listbox" aria-expanded={open} aria-controls={listId}
        style={{ width: '100%', textAlign: 'left', cursor: 'pointer', color: selected ? undefined : 'var(--acc-muted)' }}
        onClick={() => setOpen(!open)}>
        {label} <span style={{ float: 'right' }}>▾</span>
      </button>
      {open && pos && (
        <div style={{ position: 'fixed', zIndex: 1000, top: pos.top, left: pos.left, width: pos.width, background: 'var(--acc-surface)',
          border: '1px solid var(--acc-border)', borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,.12)' }}>
          <input className="acc-input" autoFocus placeholder="Buscar por código o nombre" value={filter} onChange={(e) => setFilter(e.target.value)}
            style={{ margin: 6, width: 'calc(100% - 12px)' }} />
          <div id={listId} role="listbox" style={{ maxHeight: 260, overflowY: 'auto' }}>
            {matches.length === 0 && <div style={{ padding: '8px 10px', color: 'var(--acc-muted)' }}>{accounts === null ? 'Cargando…' : 'Sin cuentas que coincidan.'}</div>}
            {matches.map((a) => (
              <div key={a.number} role="option" aria-selected={a.number === value}
                onMouseDown={(e) => { e.preventDefault(); pick(a.number); }}
                style={{ padding: '6px 10px', cursor: 'pointer', background: a.number === value ? 'var(--acc-info-bg)' : undefined }}>
                <strong>{a.number}</strong> — {a.name}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

let currencyCache: Promise<CurrencyDto[]> | null = null;
export function useCurrencies() {
  return useAsync(() => (currencyCache ??= lookupsApi.currencies().catch((e) => { currencyCache = null; throw e; })), []);
}
export function CurrencySelect({ value, onChange, onlyMapped = true }: { value: number | null | undefined; onChange: (v: number) => void; onlyMapped?: boolean }) {
  const { data } = useCurrencies();
  const opts = (data ?? []).filter((c) => !onlyMapped || c.mapped).map((c) => ({ value: c.id, label: `${c.name}${c.iso ? ` (${c.iso})` : ''}` }));
  return <Select value={value ?? ''} onChange={onChange} options={opts} placeholder="Moneda" />;
}

export function BankAccountSelect({ value, onChange, placeholder = 'Cuenta bancaria' }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  const { data } = useAsync<BankAccountDto[]>(() => banksApi.accounts(), []);
  const opts = (data ?? []).filter((a) => !a.status.trim().toUpperCase().startsWith('I'))
    .map((a) => ({ value: a.code, label: `${a.bankName ?? a.bankCode} · ${a.bankAccountNumber} (${a.currencyName ?? a.currencyId})` }));
  return <Select value={value} onChange={onChange} options={opts} placeholder={placeholder} />;
}

/** Plantillas activas de un módulo; vacío = la API usa la plantilla configurada para la operación. */
export function TemplateSelect({ moduleCode, value, onChange, allowDefault = true }: { moduleCode: string; value: number | null | undefined; onChange: (v: number | null) => void; allowDefault?: boolean }) {
  const { data } = useAsync(() => templatesApi.search({ moduleCode, status: 'A', pageSize: 200 }), [moduleCode]);
  const opts = (data?.items ?? []).map((t: TemplateDto) => ({ value: t.code, label: `${t.code} · ${t.description}${t.conceptMapping ? '' : ' (sin conceptos)'}` }));
  return <Select value={value ?? ''} onChange={(v) => onChange(v === ('' as unknown) ? null : (v as number))} options={opts}
    placeholder={allowDefault ? 'Plantilla configurada (por defecto)' : 'Seleccione plantilla'} />;
}
